/**
 * **`ThemeScope`** — the manufacturer-recolour seam, and the editor kit's one UI primitive.
 * A `className` pass-through plus an inline `style` that sets two CSS custom properties;
 * `routes/compendium/CompendiumDetail.tsx` wraps the compendium detail view in it to
 * recolour the manufacturer accent.
 *
 * Everything inside a `<ThemeScope accent="#…">` reads `var(--editor-accent)` /
 * `var(--editor-accent-dim)`, so a manufacturer re-skin sets the accent on one wrapping
 * element and the subtree recolours with no component fork.
 */
import type { ReactNode } from "react";

/** Inline style that points the editor accent vars at an arbitrary colour. */
function accentVars(accent: string, accentDim?: string): React.CSSProperties {
  return {
    // CSS custom properties aren't in React's CSSProperties type; the cast is the
    // sanctioned way to set them inline.
    ["--editor-accent" as string]: accent,
    ["--editor-accent-dim" as string]: accentDim ?? `color-mix(in srgb, ${accent} 25%, transparent)`,
  } as React.CSSProperties;
}

/**
 * Scope a manufacturer (or any) accent over a subtree. Everything inside reads
 * `var(--editor-accent)`, so wrapping a subtree in `<ThemeScope accent="#...">` recolours
 * it WITHOUT touching a single descendant.
 */
export function ThemeScope({
  accent,
  accentDim,
  className,
  children,
}: {
  accent: string;
  accentDim?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className} style={accentVars(accent, accentDim)}>
      {children}
    </div>
  );
}
